import { useEffect, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { useTransactions } from './useTransactions';
import { useCards } from './useCards';

const STORAGE_PREFIX = 'cal_notif_last_run_';

const todayStr = (): string => new Date().toISOString().split('T')[0];

const pad = (n: number): string => String(n).padStart(2, '0');

const formatDateBR = (dateStr: string): string => {
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

interface NotificationInsert {
  user_id: string;
  type: string;
  title: string;
  body: string;
  data: Record<string, unknown>;
  read_at: null;
}

async function generateAndInsertNotifications(
  userId: string,
  transactions: ReturnType<typeof useTransactions>['transactions'],
  cards: ReturnType<typeof useCards>['cards'],
): Promise<void> {
  const today = todayStr();
  const nowDate = new Date();
  const year = nowDate.getFullYear();
  const month = nowDate.getMonth() + 1;

  // ── 1. Check existing notifications from today to avoid duplicates ──
  const startOfDay = `${today}T00:00:00.000Z`;
  const { data: existingNotifs } = await supabase
    .from('notifications')
    .select('data')
    .eq('user_id', userId)
    .in('type', ['calendar_pending', 'calendar_overdue'])
    .gte('created_at', startOfDay);

  const existingSourceIds = new Set<string>(
    ((existingNotifs ?? []) as Array<{ data: Record<string, unknown> }>)
      .map((n) => n.data?.source_event_id as string)
      .filter(Boolean),
  );

  const toCreate: NotificationInsert[] = [];

  // ── 2. Overdue and due-today transactions (despesas) ──
  for (const t of transactions) {
    if (t.type !== 'despesa') continue;
    if (t.status === 'pago') continue;

    const isOverdue = t.date < today;
    const isDueToday = t.date === today;
    if (!isOverdue && !isDueToday) continue;

    const sourceId = `auto-expense-${t.id}`;
    if (existingSourceIds.has(sourceId)) continue;

    const type = isOverdue ? 'calendar_overdue' : 'calendar_pending';
    const amountStr = Number(t.amount).toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    });

    toCreate.push({
      user_id: userId,
      type,
      title: isOverdue
        ? `Pagamento atrasado: ${t.description}`
        : `Pagamento vence hoje: ${t.description}`,
      body: isOverdue
        ? `${amountStr} estava previsto para ${formatDateBR(t.date)} e ainda não foi pago.`
        : `${amountStr} vence hoje. Não esqueça de realizar o pagamento.`,
      data: {
        source_event_id: sourceId,
        linked_transaction_id: t.id,
        calendar_date: t.date,
        navigate_date: t.date,
        event_type: t.installments > 1 ? 'installment' : 'bill_due',
        amount: t.amount,
      },
      read_at: null,
    });
  }

  // ── 3. Card invoices due today ──
  for (const card of cards) {
    const dueDate = `${year}-${pad(month)}-${pad(Math.min(card.due_day, 28))}`;
    if (dueDate !== today) continue;

    const sourceId = `auto-card-due-${card.id}-${year}-${month}`;
    if (existingSourceIds.has(sourceId)) continue;

    const cardLabel = (card as { nickname?: string; name: string }).nickname || card.name;

    toCreate.push({
      user_id: userId,
      type: 'calendar_pending',
      title: `Fatura vence hoje: ${cardLabel}`,
      body: `A fatura do cartão ${cardLabel} vence hoje. Clique para ver no calendário.`,
      data: {
        source_event_id: sourceId,
        linked_credit_card_id: card.id,
        calendar_date: today,
        navigate_date: today,
        event_type: 'card_due',
      },
      read_at: null,
    });
  }

  if (toCreate.length === 0) return;

  await supabase.from('notifications').insert(toCreate);
}

/**
 * Runs once per day per user.
 * Generates internal notifications for overdue/due-today calendar events.
 * Avoids duplicates by checking for existing notifications with the same source_event_id today.
 */
export const useCalendarPendingNotifications = (): void => {
  const { user } = useAuth();
  const hasRun = useRef(false);

  const today = new Date();
  const year = today.getFullYear();
  const month = today.getMonth() + 1;

  const { transactions, loading: txLoading } = useTransactions({ year, month });
  const { cards, loading: cardsLoading } = useCards();

  useEffect(() => {
    if (!user) return;
    // Wait until both data sources have finished loading
    if (txLoading || cardsLoading) return;
    if (hasRun.current) return;

    const storageKey = STORAGE_PREFIX + user.id;
    const lastRun = localStorage.getItem(storageKey);

    if (lastRun === todayStr()) {
      hasRun.current = true;
      return;
    }

    // Mark as ran for this session (prevent double-run in same session)
    hasRun.current = true;
    localStorage.setItem(storageKey, todayStr());

    // Fire and forget
    generateAndInsertNotifications(user.id, transactions, cards).catch((err) => {
      console.error('[useCalendarPendingNotifications] error:', err);
    });
  }, [user, transactions, cards, txLoading, cardsLoading]);
};
